import React from 'react';
import { UseFormWatch, UseFormGetValues, UseFormReset, FieldValues } from 'react-hook-form';
import { motion, AnimatePresence } from 'framer-motion';

import { Box, Text, Spinner } from '../../index';
import { useDebounce } from '../../hooks/useDebounce';

interface AutoSaveFormProps<T extends FieldValues = FieldValues> {
  watch: UseFormWatch<T>;
  getValues: UseFormGetValues<T>;
  reset: UseFormReset<T>;
  onSave: (data: T) => Promise<void>;
  debounceMs?: number;
  enabled?: boolean;
  onSaveStart?: () => void;
  onSaveSuccess?: (data: T) => void;
  onSaveError?: (error: Error) => void;
  excludeFields?: string[];
  saveIndicator?: boolean;
  minimumChanges?: number;
}

export function AutoSaveForm<T extends FieldValues = FieldValues>({
  watch,
  getValues,
  reset,
  onSave,
  debounceMs = 2000,
  enabled = true,
  onSaveStart,
  onSaveSuccess,
  onSaveError,
  excludeFields = [],
  saveIndicator = true,
  minimumChanges = 1,
}: AutoSaveFormProps<T>) {
  const [isSaving, setIsSaving] = React.useState(false);
  const [lastSaved, setLastSaved] = React.useState<Date | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [changeCount, setChangeCount] = React.useState(0);

  const lastSavedDataRef = React.useRef<T | null>(null);
  // Removed unused saveTimeoutRef

  // Watch all form values
  const watchedValues = watch();
  const debouncedValues = useDebounce(watchedValues, debounceMs);

  // Check if data has changed
  const hasChanges = React.useCallback(
    (currentData: T, savedData: T | null): boolean => {
      if (!savedData) return true;

      const currentKeys = Object.keys(currentData as Record<string, unknown>).filter(
        (key) => !excludeFields.includes(key)
      );

      let changedFields = 0;

      for (const key of currentKeys) {
        if (
          JSON.stringify((currentData as Record<string, unknown>)[key]) !==
          JSON.stringify((savedData as Record<string, unknown>)[key])
        ) {
          changedFields++;
        }
      }

      return changedFields >= minimumChanges;
    },
    [excludeFields, minimumChanges]
  );

  // Auto-save logic
  React.useEffect(() => {
    if (!enabled) return;

    const currentData = getValues();

    if (!hasChanges(currentData, lastSavedDataRef.current)) {
      return;
    }

    const performSave = async () => {
      setIsSaving(true);
      setError(null);
      onSaveStart?.();

      try {
        await onSave(currentData);
        lastSavedDataRef.current = currentData;
        setLastSaved(new Date());
        setChangeCount(0);
        reset(currentData); // Reset form state to saved values
        onSaveSuccess?.(currentData);
      } catch (err) {
        const error = err as Error;
        setError(error.message || 'Kaydetme hatası');
        onSaveError?.(error);
      } finally {
        setIsSaving(false);
      }
    };

    performSave();
  }, [
    debouncedValues,
    enabled,
    getValues,
    hasChanges,
    onSave,
    onSaveStart,
    onSaveSuccess,
    onSaveError,
    reset,
  ]);

  // Track changes
  React.useEffect(() => {
    const currentData = getValues();
    if (hasChanges(currentData, lastSavedDataRef.current)) {
      setChangeCount((prev) => prev + 1);
    }
  }, [watchedValues, getValues, hasChanges]);

  // Format last saved time
  const formatLastSaved = (date: Date) => {
    const now = new Date();
    const diff = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diff < 60) return 'Az önce';
    if (diff < 3600) return `${Math.floor(diff / 60)} dakika önce`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} saat önce`;

    return date.toLocaleDateString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!saveIndicator) return null;

  return (
    <AnimatePresence mode="wait">
      <Box position="fixed" bottom="24px" right="24px" zIndex={1000}>
        {isSaving && (
          <motion.div
            key="saving"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
          >
            <Box
              display="flex"
              alignItems="center"
              backgroundColor="#3B82F6"
              color="#FFFFFF"
              padding="12px 16px"
              borderRadius="8px"
              boxShadow="0 4px 6px -1px rgba(0, 0, 0, 0.1)"
            >
              <Box marginRight="8px">
                <Spinner size="sm" color="#FFFFFF" />
              </Box>
              <Text fontSize="14px">Kaydediliyor...</Text>
            </Box>
          </motion.div>
        )}

        {!isSaving && lastSaved && !error && (
          <motion.div
            key="saved"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
          >
            <Box
              display="flex"
              alignItems="center"
              backgroundColor="#10B981"
              color="#FFFFFF"
              padding="12px 16px"
              borderRadius="8px"
              boxShadow="0 4px 6px -1px rgba(0, 0, 0, 0.1)"
            >
              <Box marginRight="8px">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M10.97 4.97a.75.75 0 011.07 1.05l-3.99 4.99a.75.75 0 01-1.08.02L4.324 8.384a.75.75 0 111.06-1.06l2.094 2.093 3.473-4.425a.267.267 0 01.02-.022z" />
                </svg>
              </Box>
              <Text fontSize="14px">Kaydedildi • {formatLastSaved(lastSaved)}</Text>
            </Box>
          </motion.div>
        )}

        {error && (
          <motion.div
            key="error"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
          >
            <Box
              display="flex"
              alignItems="center"
              backgroundColor="#DC2626"
              color="#FFFFFF"
              padding="12px 16px"
              borderRadius="8px"
              boxShadow="0 4px 6px -1px rgba(0, 0, 0, 0.1)"
            >
              <Box marginRight="8px">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M8 15A7 7 0 118 1a7 7 0 010 14zm0 1A8 8 0 108 0a8 8 0 000 16z" />
                  <path d="M7.002 11a1 1 0 112 0 1 1 0 01-2 0zM7.1 4.995a.905.905 0 111.8 0l-.35 3.507a.552.552 0 01-1.1 0L7.1 4.995z" />
                </svg>
              </Box>
              <Text fontSize="14px">{error}</Text>
            </Box>
          </motion.div>
        )}

        {changeCount > 0 && !isSaving && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            style={{
              position: 'absolute',
              top: '-8px',
              right: '-8px',
              backgroundColor: '#F59E0B',
              color: '#FFFFFF',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              fontWeight: '600',
            }}
          >
            {changeCount}
          </motion.div>
        )}
      </Box>
    </AnimatePresence>
  );
}

// Helper hook for auto-save functionality
export function useAutoSave<T = unknown>({
  getValues,
  onSave,
  debounceMs = 2000,
  enabled = true,
}: {
  getValues: () => T;
  onSave: (data: T) => Promise<void>;
  debounceMs?: number;
  enabled?: boolean;
}) {
  const [saveStatus, setSaveStatus] = React.useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [lastSaved, setLastSaved] = React.useState<Date | null>(null);
  const [error, setError] = React.useState<Error | null>(null);

  const values = getValues();
  const debouncedValues = useDebounce(values, debounceMs);

  React.useEffect(() => {
    if (!enabled || saveStatus === 'saving') return;

    const save = async () => {
      setSaveStatus('saving');
      setError(null);

      try {
        await onSave(debouncedValues);
        setSaveStatus('saved');
        setLastSaved(new Date());
      } catch (err) {
        setSaveStatus('error');
        setError(err as Error);
      }
    };

    save();
  }, [debouncedValues, enabled, onSave, saveStatus]);

  return {
    saveStatus,
    lastSaved,
    error,
    isSaving: saveStatus === 'saving',
    hasSaved: saveStatus === 'saved',
    hasError: saveStatus === 'error',
  };
}
